// _manifest.js — the single source of gallery truth for the prototype gallery.
//
// One entry per prototype tile. 132B–E APPEND entries here (one per new prototype);
// never edit a sibling's entry. The gallery (DashboardPrototypes.js) renders entirely
// from PROTOTYPE_MANIFEST; manifestIntegrity.js (WP6) validates this file on build/CI.
//
// Entry shape: { family, id, name, route, description, effects:[], kind, status }
//   family  — one of FAMILIES; groups tiles into collapsible sections + drives the family filter
//   kind    — 'enhance' (polish an existing surface) | 'rethink' (reimagine it)
//   effects — list of KNOWN_EFFECTS handles (drives the effect-tag pills); [] when none
//   status  — 'ready' | 'pending'
//
// Legacy note: the 10 legacy prototype tiles (5 dashboards + 5 dossiers) were migrated
// here losslessly from the old hardcoded DASHBOARD_VARIATIONS / DOSSIER_VARIATIONS arrays
// (routes/names/descriptions verbatim). The 4 legacy THEMES are NOT tiles — they live in
// LEGACY_THEMES below as theme-switcher data.

// Family ids (manifestIntegrity asserts every entry.family ∈ FAMILIES).
// cc/asset/scan/landing get their 10-each tiles in 132B–E; themes is switcher-only (no tiles).
export const FAMILIES = ['landing', 'cc', 'asset', 'scan', 'dashboards', 'dossiers', 'themes'];

// Human labels for the family tab/filter bar + section headers.
export const FAMILY_LABELS = {
    landing: 'Landing',
    cc: 'Command Center',
    asset: 'Asset Details',
    scan: 'Fast Scan',
    dashboards: 'Dashboards',
    dossiers: 'Dossiers',
    themes: 'Themes',
};

// Known /effects handles (manifestIntegrity asserts every entry.effects ⊆ KNOWN_EFFECTS).
// Mirrors the keyframes/utilities shipped in _shared/prototypeEffects.css (WP5) + useCountUp.
export const KNOWN_EFFECTS = [
    'shimmer',
    'gradient-border',
    'aurora',
    'marquee',
    'reveal',
    'tilt',
    'glow-card',
    'bento',
    'count-up',
    // PRD-132B WP0 additions (5 new shared effects for the L11–L14 reactbits variants):
    'iridescence',
    'gradient-text',
    'dot-grid',
    'star-border',
    'decrypt-text',
];

// Valid kinds.
export const KINDS = ['enhance', 'rethink'];

// Legacy theme-switcher data (the 4 original THEMES — preserved as switcher data, NOT tiles).
export const LEGACY_THEMES = [
    { id: 'warm', name: 'Blade Runner', description: 'Warmer dark theme with brown undertones',
      colors: { bg: '#1a1916', border: '#3d3933', accent: '#e63946' } },
    { id: 'cool', name: 'The Grid', description: 'Blue-tinted Tron-inspired theme',
      colors: { bg: '#0f1419', border: '#3a4654', accent: '#00d9ff' } },
    { id: 'neutral', name: 'Cyber Punk', description: 'Clean neutral grays for enterprise',
      colors: { bg: '#121212', border: '#333333', accent: '#dc3545' } },
    { id: 'soft', name: 'Eclipse', description: 'Muted colors inspired by code editors',
      colors: { bg: '#181a1f', border: '#3a3f4a', accent: '#e06c75' } },
];

export const PROTOTYPE_MANIFEST = [
    // ── Legacy: Dashboards (migrated from DASHBOARD_VARIATIONS; routes UNCHANGED) ──
    { family: 'dashboards', id: 'mission-control', name: 'Mission Control',
      route: '/dashboard-v1',
      description: 'NASA/SOC inspired with central gauge and satellite metrics',
      effects: [], kind: 'rethink', status: 'ready' },
    { family: 'dashboards', id: 'intelligence-brief', name: 'Intelligence Brief',
      route: '/dashboard-v2',
      description: 'Document-style layout with executive summary focus',
      effects: [], kind: 'enhance', status: 'ready' },
    { family: 'dashboards', id: 'threat-map', name: 'Threat Map',
      route: '/dashboard-v3',
      description: 'Visual/graphical centerpiece with interactive elements',
      effects: [], kind: 'rethink', status: 'ready' },
    { family: 'dashboards', id: 'analyst-workbench', name: 'Analyst Workbench',
      route: '/dashboard-v4',
      description: 'Dense information display for power users',
      effects: [], kind: 'rethink', status: 'ready' },
    { family: 'dashboards', id: 'clean-modern', name: 'Clean Modern',
      route: '/dashboard-v5',
      description: 'Consumer-app polish with generous spacing',
      effects: [], kind: 'enhance', status: 'ready' },

    // ── Legacy: Dossiers (migrated from DOSSIER_VARIATIONS; routes UNCHANGED) ──
    // Dossier routes need a /:assetId suffix; the gallery appends selectedAssetId at render.
    { family: 'dossiers', id: 'classic', name: 'Classic Dossier',
      route: '/asset-details-v1',
      description: 'Tab-based sections with formal document feel',
      effects: [], kind: 'rethink', status: 'ready' },
    { family: 'dossiers', id: 'scroll', name: 'Single Page Scroll',
      route: '/asset-details-v2',
      description: 'All information on one scrolling page with sticky nav',
      effects: [], kind: 'rethink', status: 'ready' },
    { family: 'dossiers', id: 'grid', name: 'Card Grid',
      route: '/asset-details-v3',
      description: 'Dashboard-style expandable card modules',
      effects: [], kind: 'rethink', status: 'ready' },
    { family: 'dossiers', id: 'redux', name: 'Redux (PRD-43)',
      route: '/asset-details-redux',
      description: 'Actionable intelligence first + pivot actions + progressive disclosure',
      effects: [], kind: 'enhance', status: 'ready' },
    { family: 'dossiers', id: 'redux-v2', name: 'Redux V2 (PRD-43)',
      route: '/asset-details-redux-v2',
      description: 'V1 + user feedback: technologies card, metric↔data linking, trend chart, bottom tabs',
      effects: [], kind: 'enhance', status: 'ready' },

    // ── New: 132A framework example (the canonical, permanent smoke test; 132B–E copy it) ──
    { family: 'landing', id: 'landing-00', name: 'Framework Smoke Test',
      route: '/proto/landing-00',
      description: 'PrototypeFrame + fixture + effect-CSS smoke test — the framework reference 132B–E copy',
      effects: ['aurora', 'reveal'], kind: 'enhance', status: 'ready' },

    // ── 132B–E append their 40 prototype entries below this line ──

    // ── 132B Batch A (Landing, enhance) ──
    { family: 'landing', id: 'landing-01', name: 'Aurora Hero',
      route: '/proto/landing-01',
      description: 'Split hero + proof bar with a drifting aurora background, gradient-border CTA, and count-up stats',
      effects: ['aurora', 'gradient-border', 'count-up'], kind: 'enhance', status: 'ready' },
    { family: 'landing', id: 'landing-02', name: 'Spotlight Features',
      route: '/proto/landing-02',
      description: 'Feature grid where each card is a pointer-tracked glow-card that reveals on scroll with a tilt lift',
      effects: ['glow-card', 'reveal', 'tilt'], kind: 'enhance', status: 'ready' },
    { family: 'landing', id: 'landing-03', name: 'Live Threat Ticker',
      route: '/proto/landing-03',
      description: 'Hero plus a full-bleed marquee of severity-colored findings in a gradient-border frame with a 24h count-up badge',
      effects: ['marquee', 'gradient-border', 'count-up'], kind: 'enhance', status: 'ready' },
    { family: 'landing', id: 'landing-04', name: 'Neon Grid',
      route: '/proto/landing-04',
      description: 'Aceternity-style neon card wall — animated conic gradient-borders, tilt lift, reveal entrance, count-up proof',
      effects: ['gradient-border', 'tilt', 'reveal', 'count-up'], kind: 'enhance', status: 'ready' },
    { family: 'landing', id: 'landing-05', name: 'Shimmer Load',
      route: '/proto/landing-05',
      description: 'First-paint choreography — shimmer skeletons resolve hero to proof to features with count-up on resolve',
      effects: ['shimmer', 'reveal', 'count-up'], kind: 'enhance', status: 'ready' },

    // ── 132B Batch B (Landing, enhance L6 + rethink L7–L10) ──
    { family: 'landing', id: 'landing-06', name: 'Bento Proof',
      route: '/proto/landing-06',
      description: 'Proof bar reimagined as a bento grid of mixed-size count-up glow tiles',
      effects: ['bento', 'count-up', 'glow-card'], kind: 'enhance', status: 'ready' },
    { family: 'landing', id: 'landing-07', name: 'Scan-First Front Door',
      route: '/proto/landing-07',
      description: 'The fast-scan input is the hero — a simulated stepped scan over fixture data reveals a derived results preview (no live call)',
      effects: ['count-up', 'reveal', 'shimmer', 'gradient-border', 'aurora'], kind: 'rethink', status: 'ready' },
    { family: 'landing', id: 'landing-08', name: 'Interactive Attack-Surface',
      route: '/proto/landing-08',
      description: 'Scroll-driven radial attack-surface diagram — 6 flat-count metric nodes reveal around a root with hover glow and count-up',
      effects: ['reveal', 'glow-card', 'tilt', 'count-up'], kind: 'rethink', status: 'ready' },
    { family: 'landing', id: 'landing-09', name: 'Problem → Proof Scrollytelling',
      route: '/proto/landing-09',
      description: 'Full-height narrative — problem to stakes to proof to CTA — each section reveals on scroll over an aurora bg with a drift marquee and count-up payoff',
      effects: ['reveal', 'count-up', 'aurora', 'marquee'], kind: 'rethink', status: 'ready' },
    { family: 'landing', id: 'landing-10', name: 'Terminal Simulated-Scan Hero',
      route: '/proto/landing-10',
      description: 'A terminal window streams a simulated DriftAlarm scan from fixture steps, then reveals a count-up summary and CTA (no live call)',
      effects: ['count-up', 'shimmer', 'reveal', 'gradient-border'], kind: 'rethink', status: 'ready' },

    // ── 132B Batch C (Landing, education-first reactbits additions) ──
    { family: 'landing', id: 'landing-11', name: 'Iridescence Explainer Hero',
      route: '/proto/landing-11',
      description: 'Education-first hero — a CSS iridescence field behind a gradient-text definition of attack-surface drift with a calm count-up proof row',
      effects: ['iridescence', 'gradient-text', 'reveal', 'count-up'], kind: 'rethink', status: 'ready' },
    { family: 'landing', id: 'landing-12', name: 'How Drift Happens',
      route: '/proto/landing-12',
      description: 'A dot-grid constellation where a drift lifecycle reveals step-by-step from real findings with a gradient-border active stage and count-up surface counts',
      effects: ['dot-grid', 'reveal', 'count-up', 'gradient-border'], kind: 'rethink', status: 'ready' },
    { family: 'landing', id: 'landing-13', name: 'What We Monitor',
      route: '/proto/landing-13',
      description: 'A calm tilt/glow-card coverage grid explaining the 41 rules in plain English, accented with a traveling star-border',
      effects: ['tilt', 'glow-card', 'star-border', 'reveal'], kind: 'enhance', status: 'ready' },
    { family: 'landing', id: 'landing-14', name: 'Decrypt Your Exposure',
      route: '/proto/landing-14',
      description: 'A decrypt-text scramble hero over an iridescence field resolves into a plain-language explainer of external exposure',
      effects: ['decrypt-text', 'iridescence', 'reveal'], kind: 'rethink', status: 'ready' },

    // ── 132C Batch 1 (Command Center, enhance) ──
    { family: 'cc', id: 'cc-01', name: 'Glow Metrics', route: '/proto/cc-01',
      description: "Today's CC overview: 3 pointer-tracked glow metric cards with count-up headlines + a static Security Trend echo.",
      effects: ['glow-card', 'count-up'], kind: 'enhance', status: 'ready' },
    { family: 'cc', id: 'cc-02', name: 'Aurora + Bento', route: '/proto/cc-02',
      description: 'Aurora header band over the Command Center title; overview reflowed into a bento grid with a hero risk-trend area chart.',
      effects: ['aurora', 'bento'], kind: 'enhance', status: 'ready' },
    { family: 'cc', id: 'cc-03', name: 'Live Drift Marquee', route: '/proto/cc-03',
      description: 'Looping live drift-activity ticker pinned below the CC header + 3 tilt metric cards; SOC tone.',
      effects: ['marquee', 'tilt'], kind: 'enhance', status: 'ready' },

    // ── 132C Batch 2 (Command Center, enhance) ──
    { family: 'cc', id: 'cc-04', name: 'Gradient Critical KPI', route: '/proto/cc-04',
      description: 'CC layout where only the critical/at-risk KPI carries a severity-tinted animated gradient border; sections reveal on scroll.',
      effects: ['gradient-border', 'reveal'], kind: 'enhance', status: 'ready' },
    { family: 'cc', id: 'cc-05', name: 'Shimmer Choreo', route: '/proto/cc-05',
      description: 'Loading-state showcase: layout-matched shimmer skeletons swap to fixture content with a staggered reveal.',
      effects: ['shimmer', 'reveal'], kind: 'enhance', status: 'ready' },
    { family: 'cc', id: 'cc-06', name: 'Tilt + Glow Assets', route: '/proto/cc-06',
      description: 'Assets table rows + 3 metric cards gain CC house tilt-lift + hover glow — a tactile "make the table feel alive" pass.',
      effects: ['tilt', 'glow-card'], kind: 'enhance', status: 'ready' },

    // ── 132C Batch 3 (Command Center, rethink) ──
    { family: 'cc', id: 'cc-07', name: 'Triage Worklist', route: '/proto/cc-07',
      description: 'Collapses the six CC sections into ONE severity-ranked action worklist merging topRisks, rising-risk assets, and open drift.',
      effects: ['reveal', 'tilt', 'count-up'], kind: 'rethink', status: 'ready' },
    { family: 'cc', id: 'cc-08', name: 'Map-centric', route: '/proto/cc-08',
      description: 'Mission-control: a Recharts risk-map of assets (risk vs baseline) as hero, glow-card satellite tiles orbiting.',
      effects: ['aurora', 'glow-card', 'count-up'], kind: 'rethink', status: 'ready' },
    { family: 'cc', id: 'cc-09', name: 'Timeline-first', route: '/proto/cc-09',
      description: 'Drift timeline as the primary axis: full-width risk spine + event markers; current metrics read as end-of-timeline state.',
      effects: ['reveal', 'gradient-border', 'count-up'], kind: 'rethink', status: 'ready' },
    { family: 'cc', id: 'cc-10', name: 'Focus Mode', route: '/proto/cc-10',
      description: 'One-metric-at-a-time guided dashboard for non-experts: Prev/Next through 4 plain-language focal steps.',
      effects: ['count-up', 'reveal', 'aurora'], kind: 'rethink', status: 'ready' },

    // ── 132D Batch 1 (Asset Details, enhance) ──
    { family: 'asset', id: 'asset-01', name: 'Glow Dossier', route: '/proto/asset-01',
      description: "Today's dense Asset Details layout, alive: the 6-card metric row becomes pointer-tracked glow cards with severity-keyed spotlights + count-up values.",
      effects: ['glow-card', 'count-up', 'tilt'], kind: 'enhance', status: 'ready' },
    { family: 'asset', id: 'asset-02', name: 'Aurora Profile', route: '/proto/asset-02',
      description: 'Aurora-backed identity hero over the asset profile; Attack Surface / Executive Summary / Remediation / Hardening sections reveal on scroll.',
      effects: ['aurora', 'reveal', 'tilt'], kind: 'enhance', status: 'ready' },
    { family: 'asset', id: 'asset-03', name: 'Gradient Severity', route: '/proto/asset-03',
      description: 'Remediation + dominant metric cards gain animated conic gradient borders keyed to severity — critical/high animate, medium calmer, low static.',
      effects: ['gradient-border'], kind: 'enhance', status: 'ready' },

    // ── 132D Batch 2 (Asset Details, enhance) ──
    { family: 'asset', id: 'asset-04', name: 'Bento Asset', route: '/proto/asset-04',
      description: 'Metric + viz row re-laid as a bento grid: hero Risk Score tile, wide theme-aware risk-trend chart, tall drift strip, small count-up KPIs.',
      effects: ['bento', 'count-up', 'tilt'], kind: 'enhance', status: 'ready' },
    { family: 'asset', id: 'asset-05', name: 'Reveal Tabs', route: '/proto/asset-05',
      description: 'Keeps the 2-level Discovery/Findings/Intelligence/History tab system; adds cross-fade panel swaps + reveal-on-scroll for dense tab bodies.',
      effects: ['reveal'], kind: 'enhance', status: 'ready' },
    { family: 'asset', id: 'asset-06', name: 'Shimmer Detail', route: '/proto/asset-06',
      description: 'Heavy-load choreography: skeleton-on-mount shimmer placeholders resolve region-by-region with reveal + count-up as each "loads".',
      effects: ['shimmer', 'reveal', 'count-up'], kind: 'enhance', status: 'ready' },

    // ── 132D Batch 3 (Asset Details, rethink) ──
    { family: 'asset', id: 'asset-07', name: 'Story of the Asset', route: '/proto/asset-07',
      description: 'Discards tabs for a chronological narrative spine from the drift timeline; each event reveals with score delta + plain-English summary.',
      effects: ['reveal', 'count-up', 'gradient-border', 'aurora'], kind: 'rethink', status: 'ready' },
    { family: 'asset', id: 'asset-08', name: 'Risk-first Triage', route: '/proto/asset-08',
      description: 'Tabs replaced by a prioritized open-findings worklist (exploitability→severity) with a compact identity rail; top finding pulses.',
      effects: ['gradient-border', 'glow-card', 'reveal', 'count-up'], kind: 'rethink', status: 'ready' },

    // ── 132D Batch 4 (Asset Details, rethink) ──
    { family: 'asset', id: 'asset-09', name: 'Attack-path Graph', route: '/proto/asset-09',
      description: 'Bespoke SVG node-link exposure graph: asset → ports/subdomains/tech → findings, edges severity-weighted from the theme palette; click a node for detail.',
      effects: ['reveal', 'glow-card', 'gradient-border', 'tilt'], kind: 'rethink', status: 'ready' },
    { family: 'asset', id: 'asset-10', name: 'Before/After Diff', route: '/proto/asset-10',
      description: 'Two-scan diff as the headline: per-severity + risk-score deltas count old→new, added/resolved findings, regression vs improvement border, summary ticker.',
      effects: ['count-up', 'reveal', 'gradient-border', 'marquee'], kind: 'rethink', status: 'ready' },

    // ── 132E Batch 1 (Fast Scan Results, enhance) ──
    { family: 'scan', id: 'scan-01', name: 'Glow Findings', route: '/proto/scan-01',
      description: "Today's results flow, but each vulnerability card becomes a pointer-tracked glow card tinted to its severity, with a conic gradient border on critical/high findings and a glowing risk-score chip.",
      effects: ['glow-card', 'gradient-border', 'tilt'], kind: 'enhance', status: 'ready' },
    { family: 'scan', id: 'scan-02', name: 'Aurora Brief', route: '/proto/scan-02',
      description: 'The AI executive brief is the hero: an aurora backdrop behind the threat-level + confidence chips, with the summary, key findings, and recommendations revealing on scroll.',
      effects: ['aurora', 'reveal'], kind: 'enhance', status: 'ready' },
    { family: 'scan', id: 'scan-03', name: 'Counter Summary', route: '/proto/scan-03',
      description: 'Attack-surface + summary stat tiles and the risk score count up from zero as they scroll into view — open ports, subdomains, endpoints, technologies, parameters, vulnerabilities.',
      effects: ['count-up', 'reveal'], kind: 'enhance', status: 'ready' },

    // ── 132E Batch 2 (Fast Scan Results, enhance) ──
    { family: 'scan', id: 'scan-04', name: 'Bento Surface', route: '/proto/scan-04',
      description: 'Attack Surface Analysis re-laid as a bento grid: a hero ports/services tile, span-2 subdomains + technologies, a tall endpoints tile, small HTTPS/WAF tiles — each a glow card that lifts on hover.',
      effects: ['bento', 'tilt', 'glow-card'], kind: 'enhance', status: 'ready' },
    { family: 'scan', id: 'scan-05', name: 'Reveal Cascade', route: '/proto/scan-05',
      description: 'The whole results page choreographed: Target Profile → Attack Surface → Brief → Vulnerabilities → Defensive Posture → Recommendations fade/slide in one after another with a "section complete" cue.',
      effects: ['reveal', 'tilt'], kind: 'enhance', status: 'ready' },
    { family: 'scan', id: 'scan-06', name: 'Shimmer Stream', route: '/proto/scan-06',
      description: 'Simulates a streaming scan: every section starts as a shimmer skeleton and resolves to real content on a staggered timer (Target Profile first, Vulnerabilities last) as stats count up.',
      effects: ['shimmer', 'reveal', 'count-up'], kind: 'enhance', status: 'ready' },

    // ── 132E Batch 3 (Fast Scan Results, rethink) ──
    { family: 'scan', id: 'scan-07', name: 'Scan-story', route: '/proto/scan-07',
      description: 'A guided narrative scroll that walks the findings in order of danger — one severity-tinted scene per finding pairing the vuln with a plain-English "why it matters", building to the AI verdict and top fix.',
      effects: ['reveal', 'aurora', 'gradient-border', 'count-up'], kind: 'rethink', status: 'ready' },
    { family: 'scan', id: 'scan-08', name: 'Triage Worklist', route: '/proto/scan-08',
      description: "Findings as a security analyst's queue: a dense sortable/filterable worklist with severity + effort/impact badges and inert action buttons, beside a left rail of severity counts and open-items tally.",
      effects: ['glow-card', 'count-up', 'reveal', 'tilt'], kind: 'rethink', status: 'ready' },

    // ── 132E Batch 4 (Fast Scan Results, rethink) ──
    { family: 'scan', id: 'scan-09', name: 'Exec One-Pager', route: '/proto/scan-09',
      description: 'A single-screen, shareable executive summary card — target + big risk score, a severity distribution strip, the AI one-paragraph verdict, top-3 priorities, and an inert share/export — the kind a CISO forwards.',
      effects: ['gradient-border', 'count-up', 'aurora', 'tilt'], kind: 'rethink', status: 'ready' },
    { family: 'scan', id: 'scan-10', name: 'Conversion-Optimized', route: '/proto/scan-10',
      description: 'The unauthenticated→signup funnel reimagined: top findings shown free as glow cards, the rest teased behind a blurred shimmer gate, a persistent aurora signup rail + sticky "save these results" CTA.',
      effects: ['aurora', 'gradient-border', 'shimmer', 'glow-card', 'count-up'], kind: 'rethink', status: 'ready' },
];
